import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ## One Escape closes one dialog
 *
 * Every open dialog used to attach its own `keydown` listener to `document`, so
 * a dialog opened on top of another (settle → **Take payment**, a table dialog →
 * **Move session**) had two listeners watching the same key: one press ran both
 * and the cashier lost the surface underneath as well as the one they meant to
 * back out of.
 *
 * The fix is a module-level stack of the dialogs that are currently open, plus
 * **one** listener on `document` that only ever closes the innermost of them.
 *
 * "Innermost" is not simply "registered last". Registration order is the order
 * React runs effects in, which is bottom-up within a commit — two dialogs that
 * mount together register child-first — and a dialog that re-registers (because
 * `open` flipped) would jump the queue. So the top is resolved from the DOM
 * where the DOM can answer: a dialog whose surface *contains* another open
 * dialog's surface is by definition underneath it. Among the ones left — real
 * siblings, and portalled overlays that are nobody's descendant — the last to
 * register is the top, which is the order they appeared on screen.
 *
 * An entry is removed in the effect's cleanup, so a dialog unmounted while still
 * open (its parent re-rendered it away, a route changed) takes its entry with
 * it. StrictMode's double-invoke is a mount → unmount → mount of the same
 * effect, and each of those steps pushes or splices exactly one entry, so the
 * stack ends up the same size it would in production.
 */
interface DialogStackEntry {
  /** The dialog's own surface — the containment test reads from this. */
  node: HTMLElement | null;
  close: () => void;
}

const stack: DialogStackEntry[] = [];

/** The dialog an Escape belongs to, or `null` when nothing is open. */
function innermost(): DialogStackEntry | null {
  let top: DialogStackEntry | null = null;
  for (const entry of stack) {
    const wrapsAnother = stack.some(
      (other) =>
        other !== entry && entry.node !== null && other.node !== null && entry.node.contains(other.node),
    );
    if (!wrapsAnother) top = entry;
  }
  return top;
}

function onDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  const top = innermost();
  if (!top) return;
  // A modal owns Escape while it is up: the floor-map editor listens on
  // `window`, one bubble step further out, and used to clear the operator's
  // selection behind a dialog they were only dismissing.
  event.stopPropagation();
  top.close();
}

function push(entry: DialogStackEntry) {
  stack.push(entry);
  if (stack.length === 1) document.addEventListener('keydown', onDocumentKeyDown);
}

function drop(entry: DialogStackEntry) {
  const at = stack.indexOf(entry);
  if (at !== -1) stack.splice(at, 1);
  if (stack.length === 0) document.removeEventListener('keydown', onDocumentKeyDown);
}

/**
 * Take part in the Escape stack without being a `<Dialog>`.
 *
 * `PrintableCheck` is the case this exists for: it portals its own overlay to
 * `<body>`, so it is not a DOM descendant of the dialog it opens over and the
 * containment test cannot see the nesting — but it registered later, which is
 * exactly what the stack falls back on.
 *
 * Returns the ref to put on the overlay's outermost node. It may stay `null`
 * (nothing to attach it to); such an entry simply relies on registration order.
 */
export function useDialogEscape(onClose: () => void, enabled = true) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  // Held in a ref so an inline `onClose={() => …}` — which every call site
  // writes — does not re-register the dialog on each render of the page above
  // and shuffle it to the top of the stack.
  const closeRef = React.useRef(onClose);
  React.useEffect(() => {
    closeRef.current = onClose;
  });

  React.useEffect(() => {
    if (!enabled) return;
    const entry: DialogStackEntry = {
      node: ref.current,
      close: () => closeRef.current(),
    };
    push(entry);
    return () => drop(entry);
  }, [enabled]);

  return ref;
}

/** Test-only view of the stack; nothing in the app reads it. */
export function __dialogStackDepth() {
  return stack.length;
}

/**
 * Everything `aria-modal="true"` promises but does not deliver.
 *
 * The attribute tells a screen reader the rest of the page is inert; it does not
 * make it so. Focus stayed on whatever opened the dialog, and Tab walked out of
 * it into the page underneath — still rendered, still tabbable, and now being
 * driven by someone who cannot see where they are.
 *
 * `[hidden]` and `:disabled` are filtered, but visibility is not: jsdom does no
 * layout, so an `offsetParent` test would find nothing focusable anywhere and
 * the trap would quietly do nothing in exactly the tests meant to prove it.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

function useFocusTrap(surface: React.RefObject<HTMLElement | null>, open: boolean) {
  React.useEffect(() => {
    if (!open) return;
    const node = surface.current;
    const restoreTo = document.activeElement as HTMLElement | null;

    // Several dialogs autofocus their first input. If one already took focus,
    // moving it to the surface would undo that on every one of them.
    if (node && !node.contains(document.activeElement)) node.focus();

    return () => {
      // The opener can have gone with the dialog — a row that was deleted, a
      // menu that closed. Focusing a detached node silently drops focus to
      // <body>, which is worse than leaving it alone.
      if (restoreTo && restoreTo.isConnected) restoreTo.focus();
    };
  }, [open, surface]);
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Minimal modal. Closes on Escape — the innermost one only — and on backdrop
 * click, locks body scroll while open, and holds focus: in on open, cycling on
 * Tab, and back to the opener on close.
 */
export function Dialog({ open, onClose, title, description, children, footer, className }: DialogProps) {
  const { t } = useTranslation();
  const surface = useDialogEscape(onClose, open);
  useFocusTrap(surface, open);

  /** Tab cycles within the innermost dialog; `stopPropagation` is what makes
   *  "innermost" true when one dialog is rendered inside another. */
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Tab') return;
    const node = surface.current;
    if (!node) return;
    event.stopPropagation();

    const items = focusable(node);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === node)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={surface}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Focusable only programmatically: the dialog takes focus on open so the
        // title is announced, without adding a stop to the Tab cycle.
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cn(
          // `outline-none`: the surface takes focus on open so the dialog is
          // announced, but it is not a target anyone aimed at, and Chrome draws
          // a focus ring around the whole panel if left to itself.
          'relative z-10 flex max-h-[85dvh] w-full max-w-lg flex-col rounded-xl bg-surface shadow-xl outline-none',
          className,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b p-5">
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {description && (
              <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-hover"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="scrollbar-thin flex-1 overflow-y-auto p-5">{children}</div>

        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t p-4">{footer}</footer>
        )}
      </div>
    </div>
  );
}
